// Invoices data layer (Phase 4, P4-WP9) — header + line items.
//
// The SINGLE interface between the app and the invoice schema
// (supabase/migrations/20260702101500_invoices.sql).
//
// SECURITY MODEL (server-enforced; mirrored here):
//   • We NEVER send company_id or invoice_number — DB triggers own them (company
//     forced/inherited; invoice_number auto = INV-00001; both immutable). RLS scopes all.
//   • Once an invoice is ISSUED (sent|partial|paid) the DB LOCKS its financial +
//     issuer-snapshot columns and its lines; only status/amount_paid/schedule/notes
//     may change (enforce_invoice_issued_lock). We mirror that: updateInvoice edits
//     drafts; lifecycle moves go through markSent / recordPayment / cancelInvoice.
//
// THE KEY FEATURE — one-click invoice from a completed Sales Order:
//   • createFromSalesOrder copies customer / lines / pricing / VAT forward and
//     SNAPSHOTS the issuer identity, bank details and terms from Business Settings
//     (WP8) onto the invoice, so later Settings edits never rewrite history. It
//     reads the Sales Order but never mutates it or touches inventory.
//
// SNAPSHOTS: business_details / bank_details / vat_breakdown are frozen jsonb on the
// header (D1/D3). amount_outstanding is NOT a column on this table — it was a
// generated column until 20260706180000_invoice_drop_legacy_amounts (6 July 2026)
// dropped it along with amount_paid; both names survive only as DERIVED columns on
// the invoice_ledger view, computed from invoice_payments and credit_notes. overdue
// is DERIVED on read (displayStatus), never stored (D6).
//
// RETURN-TYPE CONTRACT (consistent with salesOrders/quotations):
//   collection → { data, count, page?, pageSize? } · single → T|null ·
//   create/update/duplicate/createFromSalesOrder → id · lifecycle mutators → id ·
//   delete → void · all THROW a mapped Error.

import { supabase } from "./supabaseClient";
import { getSalesOrder } from "./salesOrders";
import { getBusinessSettings } from "./businessSettings";
import { addDaysISO, todayISO, daysBetweenISO } from "./dates";
import { C } from "../customers/theme";

const INV = "invoices";
const LINES = "invoice_lines";
const LEDGER = "invoice_ledger";  // P4-CC1: derived read view — amount_paid/outstanding/status from payment rows

export const DEFAULT_PAGE_SIZE = 25;

/** Real lifecycle statuses (+ DB CHECK parity). `overdue` is a DERIVED display state. */
export const INV_STATUSES = [
  { value: "draft", label: "Draft", color: C.textMuted },
  { value: "sent", label: "Sent", color: C.primary },
  { value: "partial", label: "Part-paid", color: C.warning },
  { value: "paid", label: "Paid", color: C.success },
  { value: "cancelled", label: "Cancelled", color: C.danger },
];
/** Derived-only badge shown when an unpaid issued invoice is past its due date. */
export const OVERDUE_STATUS = { value: "overdue", label: "Overdue", color: "#dc2626" };

export const PAYMENT_TERMS = [
  { value: "due_on_receipt", label: "Due on receipt", days: 0 },
  { value: "net_7", label: "Net 7 days", days: 7 },
  { value: "net_14", label: "Net 14 days", days: 14 },
  { value: "net_30", label: "Net 30 days", days: 30 },
  { value: "net_60", label: "Net 60 days", days: 60 },
  { value: "net_90", label: "Net 90 days", days: 90 },
];

export const PAYMENT_OPTIONS = [
  { value: "full", label: "Full payment" },
  { value: "deposit", label: "Deposit + balance" },
  { value: "installments", label: "Installments" },
];

export const CURRENCIES = [
  { value: "EUR", label: "EUR (€)" },
  { value: "GBP", label: "GBP (£)" },
  { value: "USD", label: "USD ($)" },
];

export const VAT_RATES = [0, 9, 13.5, 23];

export const INV_SORTS = [
  { value: "number", label: "Invoice No." },
  { value: "issue_date", label: "Issue Date" },
  { value: "due_date", label: "Due Date" },
  { value: "status", label: "Status" },
  { value: "total", label: "Total" },
  { value: "outstanding", label: "Outstanding" },
  { value: "created", label: "Newest" },
];

const HEADER_WRITE_FIELDS = [
  "sales_order_id", "sales_order_number",
  "customer_id", "customer_name", "customer_email", "customer_address", "customer_vat",
  "issue_date", "due_date", "payment_terms", "preferred_currency", "status",
  "payment_option", "payment_schedule",
  "business_details", "bank_details", "terms_text", "footer_text", "notes",
];
const LINE_WRITE_FIELDS = ["product_id", "sku", "description", "quantity", "unit_price", "discount_percent", "vat_rate", "drs", "drs_amount"];
const SORT_COLUMNS = { number: "invoice_number", issue_date: "issue_date", due_date: "due_date", status: "status", total: "total_amount", outstanding: "amount_outstanding", created: "created_at" };

// P4-CC1: list reads come from the ledger VIEW; the denormalised customer_name is
// used for display, so no customers embed is needed off the view.
const HEADER_SELECT =
  "id, invoice_number, sales_order_id, sales_order_number, customer_id, customer_name, customer_email, " +
  "issue_date, due_date, payment_terms, preferred_currency, status, subtotal, total_discount, total_vat, " +
  "total_drs, total_amount, amount_paid, amount_outstanding, payment_option, created_at, updated_at";
const FULL_SELECT =
  "*, customers ( id, company_name, payment_terms, preferred_currency, vat_number ), " +
  "invoice_lines ( id, product_id, sku, description, quantity, unit_price, discount_percent, vat_rate, drs, drs_amount, line_total, position )";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};
const sanitizeTerm = (t) => String(t || "").trim().replace(/[%,()]/g, "");
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Due date = issue date + the chosen term's day count (default net 30). */
export function dueDateFrom(issueDate, terms) {
  const t = PAYMENT_TERMS.find((x) => x.value === terms);
  return addDaysISO(issueDate || todayISO(), t ? t.days : 30);
}

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23514": message = "Invalid value — check the status or payment option."; break;
    case "23503": message = "Referenced customer, product or sales order does not exist."; break;
    case "23502": message = "A required field is missing."; break;
    case "42501": message = (error && error.message) || "This invoice is issued and its details are locked."; break;
    case "PGRST116": message = "Invoice not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// ── Totals — the local computeTotals SUPERSET (per-band VAT + DRS) ────────────
// Superset of the salesOrders/quotations computeTotals: same net/discount/VAT math,
// PLUS a per-rate vat_breakdown and DRS (Deposit Return Scheme) deposits. Kept local
// (not shared) so this WP does not touch the protected SO/QT data layers.

const lineNet = (l) => (Number(l.quantity) || 0) * (Number(l.unit_price) || 0) * (1 - (Number(l.discount_percent) || 0) / 100);
const lineDrs = (l) => (l && l.drs ? round2(Number(l.drs_amount) || 0) : 0);

/**
 * Compute per-line and document totals with a per-rate VAT breakdown and DRS.
 * line_total = net (qty · price · (1 − disc%)); VAT and DRS are excluded from it.
 * DRS deposits are added AFTER VAT (deposits are not VAT-able).
 * @param {Array} lines
 * @returns {{ lines, subtotal, total_discount, total_vat, total_drs, total_amount, vat_breakdown }}
 */
export function computeTotals(lines = []) {
  let subtotal = 0, total_discount = 0, total_vat = 0, total_drs = 0;
  const bands = new Map(); // rate → { base, vat }
  const computed = lines.map((l, i) => {
    const net = lineNet(l);
    const gross = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
    const rate = Number(l.vat_rate) || 0;
    const vat = net * (rate / 100);
    const drs = lineDrs(l);
    subtotal += net;
    total_discount += gross - net;
    total_vat += vat;
    total_drs += drs;
    const band = bands.get(rate) || { base: 0, vat: 0 };
    band.base += net; band.vat += vat;
    bands.set(rate, band);
    return { ...l, drs_amount: drs, line_total: round2(net), position: i };
  });
  const vat_breakdown = [...bands.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rate, v]) => ({ rate, base: round2(v.base), vat: round2(v.vat) }));
  return {
    lines: computed,
    subtotal: round2(subtotal),
    total_discount: round2(total_discount),
    total_vat: round2(total_vat),
    total_drs: round2(total_drs),
    // ⚠️ THE SUM OF THE ROUNDED PARTS, NOT THE ROUNDED SUM (P4-CC26c-1a).
    // This was round2(subtotal + total_vat + total_drs) — the components rounded
    // separately, the total rounded once over the unrounded sum. Since
    // round2(a) + round2(b) !== round2(a + b), the stored parts could fail to
    // add up to the stored total by a cent, with no constraint to catch it.
    // Reproduced with entirely ordinary Irish inputs: 1 x EUR 6.67 @ 0% plus
    // 1.5 x EUR 6.67 @ 9% stored subtotal 16.67 + total_vat 0.90 = 17.57 against
    // total_amount 17.58. quantity is numeric(14,3), so 1.5 is perfectly legal.
    //
    // Nobody had hit it because every seeded fixture uses integer quantities and
    // clean prices. The first customer billing 1.5 hours at EUR 6.67 would have
    // produced a journal that could not balance — post_invoice_to_ledger()
    // refuses such an invoice rather than plugging the difference, so this is
    // the fix that stops it arising in the first place.
    //
    // The identity subtotal + total_vat + total_drs = total_amount now holds by
    // CONSTRUCTION rather than by luck. Named regression test:
    // "1.5 x EUR 6.67 @ 9%" in invoices.test.js.
    total_amount: round2(round2(subtotal) + round2(total_vat) + round2(total_drs)),
    vat_breakdown,
  };
}

// ── Derived display status (overdue is never stored) ─────────────────────────

/** True when an unpaid, issued invoice is past its due date. */
export function isOverdue(inv) {
  if (!inv || !["sent", "partial"].includes(inv.status)) return false;
  return !!inv.due_date && inv.due_date < todayISO();
}
/** The status to SHOW: real status, or 'overdue' when derived. */
export function displayStatus(inv) {
  return isOverdue(inv) ? "overdue" : inv?.status;
}

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * List invoices (RLS-scoped) with search, status/customer filters, sort, pagination.
 * status='overdue' is handled as the derived filter (issued + unpaid + past due).
 * @param {Object} [opts]
 * @returns {Promise<{data:Array,count:number,page:number,pageSize:number}>}
 */
export async function getInvoices(opts = {}) {
  const { search, status, customerId, page = 1, pageSize = DEFAULT_PAGE_SIZE, sortBy = "created", sortDir = "desc" } = opts;
  const c = client();
  const column = SORT_COLUMNS[sortBy] || "created_at";
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const from = (safePage - 1) * safeSize;
  let q = c.from(LEDGER).select(HEADER_SELECT, { count: "exact" }).order(column, { ascending: sortDir !== "desc" }).range(from, from + safeSize - 1);
  if (status === "overdue") q = q.in("status", ["sent", "partial"]).lt("due_date", todayISO());
  else if (status && status !== "all") q = q.eq("status", status);
  if (customerId && customerId !== "all") q = q.eq("customer_id", customerId);
  const term = sanitizeTerm(search);
  if (term) q = q.or(`invoice_number.ilike.%${term}%,customer_name.ilike.%${term}%,sales_order_number.ilike.%${term}%`);
  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load invoices");
  return { data: data || [], count: count ?? 0, page: safePage, pageSize: safeSize };
}

export async function searchInvoices(term, opts = {}) { return getInvoices({ ...opts, search: term }); }

/** Full invoice (header + customer + lines, ordered). */
export async function getInvoice(id) {
  const c = client();
  // Header/lines from the base table (proven embeds); derived money + status from
  // the ledger view (payment rows) — merged into one row, one source of truth.
  const [{ data, error }, { data: led }] = await Promise.all([
    c.from(INV).select(FULL_SELECT).eq("id", id).maybeSingle(),
    c.from(LEDGER).select("amount_paid, amount_outstanding, status").eq("id", id).maybeSingle(),
  ]);
  if (error) throw toError(error, "Could not load invoice");
  if (!data) return null;
  if (data.invoice_lines) data.invoice_lines.sort((a, b) => (a.position || 0) - (b.position || 0));
  return led ? { ...data, amount_paid: led.amount_paid, amount_outstanding: led.amount_outstanding, status: led.status } : data;
}

/**
 * P4-CC19b — every invoice-derived KPI in the app, aggregated SERVER-SIDE in one
 * call (public.invoice_stats, 20260811120000_cc19b_aggregates.sql).
 *
 * THIS IS ALSO THE STATEMENTS SCREEN'S RECEIVABLES SOURCE. src/lib/statements.js
 * imports this function rather than computing its own total — see the migration's
 * "THE 1.48 CONSTRAINT" header for why. `outstanding` (sent/partial) and
 * `netReceivables` (all issued, credits included) are two DIFFERENT definitions
 * that disagree by the value of any credit balance; they come off one scan here
 * so they can never silently drift, and `overdueAmount` vs `overdueReceivables`
 * are a second such pair. Do not add another receivables total anywhere.
 *
 * "Today" and the month key are passed IN, never derived server-side: Postgres
 * is UTC, todayISO() is the browser's local calendar, and they are different
 * days for an hour each night under BST (see dates.js / P4-CC20).
 *
 * `count` (total invoices, any status) was dropped in CC19b — nothing consumed it.
 * @returns {Promise<{outstanding:number, netReceivables:number, overdueCount:number,
 *   overdueAmount:number, overdueReceivables:number, paidThisMonth:number,
 *   paidTotal:number, draftCount:number, customersWithBalance:number,
 *   activeCustomerCount:number}>}
 */
export async function getInvoiceStats() {
  const today = todayISO();
  const { data, error } = await client().rpc("invoice_stats", { p_today: today, p_month: today.slice(0, 7) });
  if (error) throw toError(error, "Could not load invoice stats");
  const r = (data || [])[0] || {};
  const n = (v) => Number(v) || 0;
  return {
    outstanding: n(r.open_outstanding),
    netReceivables: n(r.net_receivables),
    overdueCount: n(r.overdue_count),
    overdueAmount: n(r.overdue_amount),
    overdueReceivables: n(r.overdue_receivables),
    paidThisMonth: n(r.paid_this_month),
    paidTotal: n(r.paid_total),
    draftCount: n(r.draft_count),
    customersWithBalance: n(r.customers_with_balance),
    activeCustomerCount: n(r.active_customer_count),
  };
}

/**
 * TEMPORARY (P4-CC19b Phase 1) — the pre-CC19b client-side implementation, kept
 * ONLY so supabase/verify/cc19b_aggregates.verify.mjs can run it side by side
 * with the SQL version against identical data and diff every field. It carries
 * the silent 1,000-row truncation this package exists to remove, so nothing in
 * src/ may call it. DELETE once the parity harness has been run and accepted.
 */
export async function getInvoiceStatsLegacy() {
  const { data, error } = await client().from(LEDGER)
    .select("status, total_amount, amount_outstanding, amount_paid, issue_date, due_date")
    .range(0, 9999);
  if (error) throw toError(error, "Could not load invoice stats");
  const rows = data || [];
  const month = todayISO().slice(0, 7);
  let outstanding = 0, overdueCount = 0, overdueAmount = 0, paidThisMonth = 0, paidTotal = 0, draftCount = 0;
  for (const r of rows) {
    if (["sent", "partial"].includes(r.status)) {
      outstanding += Number(r.amount_outstanding) || 0;
      if (r.due_date && r.due_date < todayISO()) { overdueCount += 1; overdueAmount += Number(r.amount_outstanding) || 0; }
    }
    if (r.status === "paid") {
      paidTotal += Number(r.total_amount) || 0;                                        // lifetime revenue (Dashboard)
      if ((r.issue_date || "").slice(0, 7) === month) paidThisMonth += Number(r.total_amount) || 0;
    }
    if (r.status === "draft") draftCount += 1;
  }
  return {
    outstanding: round2(outstanding), overdueCount, overdueAmount: round2(overdueAmount),
    paidThisMonth: round2(paidThisMonth), paidTotal: round2(paidTotal), draftCount, count: rows.length,
  };
}

// ── Aggregates for Dashboard + VAT Return (CC2) ──────────────────────────────
// Read-only, RLS-scoped, aggregated client-side over date-bounded fetches. No new
// tables/DB objects. (Scans cap at 10k rows like getInvoiceStats; a SQL view/RPC is
// the later optimisation if a tenant ever exceeds that in a window.)

// The last `months` month keys ("YYYY-MM"), oldest → newest.
function lastMonthKeys(months) {
  const keys = [];
  const now = new Date();
  for (let k = Math.max(1, months) - 1; k >= 0; k--) {
    const m = new Date(now.getFullYear(), now.getMonth() - k, 1);
    keys.push(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`);
  }
  return keys;
}

/**
 * Revenue per month from FULLY-PAID invoices (by issue_date), oldest → newest.
 * Empty months are present with revenue 0 (so a zero-invoice company shows a flat 0 line).
 * @param {number} [months=6]
 * @returns {Promise<Array<{key:string, revenue:number}>>}
 */
export async function getRevenueByMonth(months = 6) {
  // P4-CC19b — the SUM moves server-side (public.revenue_by_month); the ZERO-FILL
  // stays here. Which months to display comes from lastMonthKeys(), i.e. the
  // browser's local calendar, and deriving that range server-side would need a
  // server "now" — the one thing the migration forbids. So SQL returns only the
  // months that have revenue, and the full key list is left-joined onto it here,
  // preserving the flat-0 line a company with no paid invoices has always shown.
  const keys = lastMonthKeys(months);
  const { data, error } = await client().rpc("revenue_by_month", { p_from: `${keys[0]}-01` });
  if (error) throw toError(error, "Could not load revenue");
  const byMonth = Object.fromEntries(keys.map((k) => [k, 0]));
  for (const r of data || []) { if (r.month_key in byMonth) byMonth[r.month_key] = Number(r.revenue) || 0; }
  return keys.map((k) => ({ key: k, revenue: round2(byMonth[k]) }));
}

/**
 * TEMPORARY (P4-CC19b Phase 1) — pre-CC19b implementation, for the parity
 * harness only. See getInvoiceStatsLegacy. Nothing in src/ may call this.
 */
export async function getRevenueByMonthLegacy(months = 6) {
  const keys = lastMonthKeys(months);
  const { data, error } = await client().from(LEDGER)
    .select("total_amount, issue_date, status").eq("status", "paid").gte("issue_date", `${keys[0]}-01`).range(0, 9999);
  if (error) throw toError(error, "Could not load revenue");
  const byMonth = Object.fromEntries(keys.map((k) => [k, 0]));
  for (const r of data || []) { const k = (r.issue_date || "").slice(0, 7); if (k in byMonth) byMonth[k] += Number(r.total_amount) || 0; }
  return keys.map((k) => ({ key: k, revenue: round2(byMonth[k]) }));
}

/**
 * The most-overdue issued+unpaid invoices (sent/partial, past due), oldest-due first,
 * each annotated with days_overdue. Empty for a company with none.
 * @param {number} [limit=5]
 * @returns {Promise<Array>}
 */
export async function getOverdueInvoices(limit = 5) {
  const { data, error } = await client().from(LEDGER)
    .select("id, invoice_number, customer_name, issue_date, due_date, total_amount, amount_outstanding, status")
    .in("status", ["sent", "partial"]).lt("due_date", todayISO()).order("due_date", { ascending: true }).limit(Math.max(1, limit));
  if (error) throw toError(error, "Could not load overdue invoices");
  return (data || []).map((r) => ({
    ...r,
    days_overdue: r.due_date ? Math.max(0, daysBetweenISO(r.due_date, todayISO())) : 0,
  }));
}

/**
 * VAT on sales (output) per rate band over [start,end] by issue_date, summed from each
 * invoice's frozen vat_breakdown. Counts issued invoices (sent|partial|paid) — never
 * draft or cancelled. Zero-safe for an empty company.
 * @param {string} start ISO date @param {string} end ISO date
 * @returns {Promise<{sales_0,sales_9,sales_13_5,sales_23,sales_total_ex_vat,total_vat_on_sales:number}>}
 */
export async function getVatSalesBreakdown(start, end) {
  // P4-CC19a-2 — count-based truncation detection. The old "range(0,10000), throw
  // if the 10001st row comes back" guard was structurally unfireable: PostgREST's
  // server-side max_rows (confirmed live: Project Settings -> Data API -> Max
  // Rows = 1000) caps EVERY response at a fixed ceiling regardless of what range a
  // client requests, so data.length could never exceed 1000 and this throw could
  // never trigger — a wrong VAT figure would have been returned silently.
  // { count: "exact" } runs an independent, uncapped COUNT(*) against the same
  // filter; comparing it to the returned row count is what actually detects it.
  let q = client().from(LEDGER).select("status, issue_date, vat_breakdown", { count: "exact" }).in("status", ["sent", "partial", "paid"]);
  if (start) q = q.gte("issue_date", start);
  if (end) q = q.lte("issue_date", end);
  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load VAT on sales");
  // count == null is untrustworthy, not "0 rows" — defaulting a missing count to
  // 0 would silently disable this guard on a statutory filing figure.
  if (count == null || count > (data || []).length) {
    const e = new Error("Too many records in this period to total automatically. Contact support before filing.");
    e.code = "TOO_MANY_ROWS";
    throw e;
  }
  const base = {}; let totalVat = 0, totalBase = 0;
  for (const r of data || []) for (const b of r.vat_breakdown || []) {
    const rate = Number(b.rate) || 0;
    base[rate] = (base[rate] || 0) + (Number(b.base) || 0);
    totalBase += Number(b.base) || 0;
    totalVat += Number(b.vat) || 0;
  }
  return {
    sales_0: round2(base[0] || 0), sales_9: round2(base[9] || 0), sales_13_5: round2(base[13.5] || 0), sales_23: round2(base[23] || 0),
    sales_total_ex_vat: round2(totalBase), total_vat_on_sales: round2(totalVat),
  };
}

// ── Writes ───────────────────────────────────────────────────────────────────

async function replaceLines(invId, lines) {
  const c = client();
  const { error: delErr } = await c.from(LINES).delete().eq("invoice_id", invId);
  if (delErr) throw toError(delErr, "Could not update line items");
  // Build UNIFORM rows: every NOT-NULL column gets an explicit value on EVERY row.
  // A PostgREST batch insert uses the union of keys across rows and writes explicit
  // NULL (never the column DEFAULT) for any row missing a key — so a single line with
  // drs/discount set would force NULLs into the others. Explicit defaults avoid that.
  const rows = (lines || [])
    .filter((l) => l.product_id || (l.description && l.description.trim()) || Number(l.quantity) > 0)
    .map((l, i) => ({
      ...pick(l, LINE_WRITE_FIELDS),
      quantity: Number(l.quantity) || 0,
      unit_price: Number(l.unit_price) || 0,
      discount_percent: Number(l.discount_percent) || 0,
      vat_rate: Number(l.vat_rate) || 0,
      drs: !!l.drs,
      drs_amount: l.drs ? round2(Number(l.drs_amount) || 0) : 0,
      line_total: round2(lineNet(l)),
      position: i,
      invoice_id: invId,
    }));
  if (rows.length) {
    const { error } = await c.from(LINES).insert(rows);
    if (error) throw toError(error, "Could not save line items");
  }
}

function headerFromPayload(payload, totals) {
  return {
    ...pick(payload || {}, HEADER_WRITE_FIELDS),
    subtotal: totals.subtotal, total_discount: totals.total_discount, total_vat: totals.total_vat,
    total_drs: totals.total_drs, total_amount: totals.total_amount, vat_breakdown: totals.vat_breakdown,
  };
}

const hasSnapshot = (o) => o && typeof o === "object" && Object.keys(o).length > 0;

/**
 * Guarantee the issuer/bank/terms snapshot is frozen onto the invoice. If the caller
 * already supplied one (an edited form, or createFromSalesOrder), keep it; otherwise
 * snapshot it from Business Settings — so a MANUAL createInvoice freezes it identically.
 */
async function ensureIssuerSnapshot(payload) {
  if (hasSnapshot(payload?.business_details) && hasSnapshot(payload?.bank_details)) return payload;
  const snap = snapshotFromSettings(await getBusinessSettings());
  return {
    ...payload,
    business_details: hasSnapshot(payload?.business_details) ? payload.business_details : snap.business_details,
    bank_details: hasSnapshot(payload?.bank_details) ? payload.bank_details : snap.bank_details,
    terms_text: payload?.terms_text != null ? payload.terms_text : snap.terms_text,
    footer_text: payload?.footer_text != null ? payload.footer_text : snap.footer_text,
  };
}

/**
 * Create an invoice with its lines. Freezes the issuer/bank/terms snapshot from
 * Business Settings when the payload doesn't already carry one.
 * @param {Object} payload Header fields + `lines: Array`.
 * @returns {Promise<string>} new invoice id.
 */
export async function createInvoice(payload) {
  const c = client();
  const full = await ensureIssuerSnapshot(payload);
  const totals = computeTotals(full?.lines || []);
  const { data, error } = await c.from(INV).insert(headerFromPayload(full, totals)).select("id").single();
  if (error) throw toError(error, "Could not create invoice");
  await replaceLines(data.id, totals.lines);
  return data.id;
}

/**
 * Update an invoice header + replace its lines. The DB rejects this once the
 * invoice is issued (financials/lines locked) — edit only while it is a draft.
 * @param {string} id @param {Object} payload Header fields + `lines: Array`.
 * @returns {Promise<string>} the invoice id.
 */
export async function updateInvoice(id, payload) {
  const c = client();
  const totals = computeTotals(payload?.lines || []);
  const { error } = await c.from(INV).update(headerFromPayload(payload, totals)).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not update invoice");
  await replaceLines(id, totals.lines);
  return id;
}

/** Delete an invoice (cascades its lines). */
export async function deleteInvoice(id) {
  const { error } = await client().from(INV).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete invoice");
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

/**
 * Post an ISSUED invoice to the general ledger (P4-CC26c-1a).
 *
 * Thin by design: every rule lives in post_invoice_to_ledger() in
 * 20260818120000_cc26c1a_post_invoice.sql, where the verification estate can
 * reach it. This maps its refusals to sentences a business owner can act on.
 *
 * POSTED-STATE IS DERIVED, never stored: an invoice is posted iff a journal
 * exists with source_type='invoice' and its id. There is deliberately no
 * `posted` column to drift out of step with the ledger.
 *
 * @param {string} id invoice id
 * @returns {Promise<string>} the journal id
 */
export async function postToLedger(id) {
  const { data, error } = await client().rpc("post_invoice_to_ledger", { p_invoice_id: id });
  if (error) throw toLedgerError(error);
  return data;
}

// Ledger refusals carry codes the generic toError() does not know about, and
// each one means something specific the user can act on. Same shape as
// toError(): switch on the code, never on the message, and keep the original.
function toLedgerError(error) {
  const code = error && error.code;
  const raw = (error && error.message) || "";
  let message;
  switch (code) {
    case "23505":
      message = "This invoice has already been posted to the ledger.";
      break;
    case "42501":
      // Either a draft/cancelled invoice, or no write permission on accounts.
      // The database's own sentence is already the clearest thing available and
      // names the invoice and its status, so it is passed through.
      message = raw || "This invoice cannot be posted.";
      break;
    case "P0002":
      message = "Invoice not found, or you do not have access to it.";
      break;
    case "P0001":
      // Two shapes: the invoice does not reconcile, or a system account is
      // missing from the chart. Both name exactly what is wrong and what to do,
      // and inventing a friendlier sentence would throw that away.
      message = raw || "This invoice could not be posted to the ledger.";
      break;
    case "23514":
      // ⚠️ 23514 IS EVERY CHECK CONSTRAINT ON journals AND journal_lines, NOT
      // ONLY post_journal()'s balance assertion. This branch used to name
      // balance outright — and when a source_type violation came through it
      // reported a balance failure, pointing whoever read it at the one thing
      // that was working. The harness only survived it because it also printed
      // the raw cause; a UI showing the mapped message alone would have sent
      // someone hunting the balance logic for hours.
      //
      // The "this is a defect" framing is still true and stays. What changes is
      // that the database's own words are CARRIED THROUGH — they contain the
      // constraint name (journals_source_type_chk, journal_lines_one_side_chk,
      // journals_source_basis_chk, …), which is the one detail that identifies
      // which rule was broken. An error mapper that discards the identifying
      // detail is the same family as a discarded HTTP status.
      message = `The ledger entry for this invoice was rejected by the database. This is a defect, not a data-entry problem — please report it, with this detail: ${raw || "(the database supplied no detail)"}`;
      break;
    default:
      message = raw || "Could not post the invoice to the ledger.";
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// ── P4-CC26c-1d — markSent / cancelInvoice stop being blind writes ──────────
// THE TRIGGER (trg_55_state_machine) IS THE GUARD; these reads exist to say
// something useful first. What is pre-checked here is only what one row can
// answer about itself — "you are asking for a transition that is illegal on
// its face". What is deliberately NOT pre-checked is payments and journals:
// those guards live in the trigger, and duplicating them here would create a
// second place for the rule to drift (the same reasoning that keeps journal
// balance in post_journal() and nowhere else).
//
// ⛔ toError MUST keep passing 42501 through with the database's sentence
// (:132). On these UPDATEs an RLS USING failure yields zero rows → PGRST116
// (already mapped), so a 42501 here can ONLY be a trigger's own instruction —
// "Issue a credit note instead", "Reverse its ledger entry first" — and
// replacing it with a generic line is CC26c-1b-fix2's disease.

/** Issue a draft invoice (draft → sent). */
export async function markSent(id) {
  const c = client();
  // The trigger is the guard; this exists to say something useful first.
  const { data: cur, error: readErr } = await c.from(INV).select("status, invoice_number").eq("id", id).maybeSingle();
  if (readErr) throw toError(readErr, "Could not mark the invoice as sent");
  if (cur) {
    const name = cur.invoice_number || "This invoice";
    if (cur.status === "sent") throw new Error(`Invoice ${name} has already been sent.`);
    if (cur.status === "cancelled") throw new Error(`Invoice ${name} is cancelled; a cancelled invoice cannot be changed.`);
    if (cur.status !== "draft") throw new Error(`Invoice ${name} cannot move from '${cur.status}' to 'sent'.`);
  }
  // cur == null (not found OR not yours) falls through on purpose: the update
  // returns zero rows and the existing PGRST116 mapping keeps that sentence
  // deliberately ambiguous, exactly as before this package.
  const { error } = await c.from(INV).update({ status: "sent" }).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not mark the invoice as sent");
  return id;
}

/** Cancel an invoice. The payments / ledger guards are the trigger's, not ours. */
export async function cancelInvoice(id) {
  const c = client();
  // The trigger is the guard; this exists to say something useful first.
  const { data: cur, error: readErr } = await c.from(INV).select("status, invoice_number").eq("id", id).maybeSingle();
  if (readErr) throw toError(readErr, "Could not cancel the invoice");
  if (cur && cur.status === "cancelled") {
    throw new Error(`Invoice ${cur.invoice_number || id} is already cancelled.`);
  }
  const { error } = await c.from(INV).update({ status: "cancelled" }).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not cancel the invoice");
  return id;
}

// ── Payments (P4-CC1) ────────────────────────────────────────────────────────
// recordPayment now INSERTS a discrete payment row (payments.js) instead of bumping
// a stored amount_paid; the invoice's paid/outstanding/status are derived on read
// via the ledger view. Re-exported so existing callers (and the CC2 harness) keep
// working with the same recordPayment(id, amount) signature.
export { recordPayment } from "./payments";

// ── Snapshots + create-from-Sales-Order ──────────────────────────────────────

/** Freeze the issuer identity, bank details and terms from Business Settings (WP8). */
export function snapshotFromSettings(s) {
  const b = s || {};
  return {
    business_details: {
      legal_name: b.legal_name || null, trading_name: b.trading_name || null,
      registration_number: b.registration_number || null, vat_number: b.vat_number || null,
      email: b.email || null, phone: b.phone || null, website: b.website || null,
      address_line1: b.address_line1 || null, address_line2: b.address_line2 || null,
      city: b.city || null, county: b.county || null, postal_code: b.postal_code || null,
      country: b.country || null, logo_url: b.logo_url || null,
    },
    bank_details: {
      bank_account_name: b.bank_account_name || null, bank_name: b.bank_name || null,
      iban: b.iban || null, bic: b.bic || null, sort_code: b.sort_code || null, account_number: b.account_number || null,
    },
    terms_text: b.default_terms_text || null,
    footer_text: b.invoice_footer || null,
    default_payment_terms: b.default_payment_terms || "net_30",
    default_currency: b.default_currency || "EUR",
  };
}

/**
 * Build a blank draft invoice pre-filled from Business Settings — used by the
 * "New Invoice" (manual) form so Your Business / Bank Details / Terms arrive filled.
 * @returns {Promise<Object>} a draft invoice shape (not persisted).
 */
export async function newInvoiceDraft() {
  const settings = await getBusinessSettings();
  const snap = snapshotFromSettings(settings);
  const issue = todayISO();
  return {
    sales_order_id: null, sales_order_number: null,
    customer_id: null, customer_name: "", customer_email: "", customer_address: "", customer_vat: "",
    issue_date: issue, due_date: dueDateFrom(issue, snap.default_payment_terms),
    payment_terms: snap.default_payment_terms, preferred_currency: snap.default_currency, status: "draft",
    payment_option: "full", payment_schedule: [],
    business_details: snap.business_details, bank_details: snap.bank_details,
    terms_text: snap.terms_text, footer_text: snap.footer_text, notes: "",
    lines: [],
    _settingsMissing: !settings,
  };
}

/**
 * THE KEY FEATURE — create an invoice from a Sales Order in one click.
 * Copies customer, lines, pricing and VAT forward; snapshots issuer/bank/terms from
 * Business Settings; links the SO. The Sales Order is read-only here — never mutated.
 * @param {string} soId
 * @returns {Promise<string>} new invoice id.
 */
export async function createFromSalesOrder(soId) {
  const so = await getSalesOrder(soId);
  if (!so) throw toError({ code: "PGRST116" }, "Sales order not found");
  const settings = await getBusinessSettings();
  const snap = snapshotFromSettings(settings);
  const cust = so.customers || {};
  // The SO's customer join is intentionally light (no vat_number) — read it straight
  // from the customer record so the invoice snapshots the customer's VAT number.
  let customerVat = "";
  if (so.customer_id) {
    const { data: cu } = await client().from("customers").select("vat_number").eq("id", so.customer_id).maybeSingle();
    customerVat = cu?.vat_number || "";
  }
  const terms = cust.payment_terms || snap.default_payment_terms;
  const issue = todayISO();
  const lines = (so.sales_order_lines || []).map((l) => pick(l, LINE_WRITE_FIELDS));
  return createInvoice({
    sales_order_id: so.id, sales_order_number: so.so_number,
    customer_id: so.customer_id, customer_name: so.customer_name,
    customer_email: "", customer_address: "", customer_vat: customerVat,
    issue_date: issue, due_date: dueDateFrom(issue, terms),
    payment_terms: terms, preferred_currency: so.preferred_currency || snap.default_currency, status: "draft",
    payment_option: "full", payment_schedule: [],
    business_details: snap.business_details, bank_details: snap.bank_details,
    terms_text: snap.terms_text, footer_text: snap.footer_text,
    notes: so.notes || "",
    lines,
  });
}

/** How many invoices already reference this Sales Order (soft duplicate warning). */
export async function countInvoicesForSalesOrder(soId) {
  const { count, error } = await client().from(INV).select("id", { count: "exact", head: true }).eq("sales_order_id", soId);
  if (error) throw toError(error, "Could not check existing invoices");
  return count ?? 0;
}

/**
 * Duplicate an invoice as a fresh DRAFT (new number, today's issue date) — same
 * customer, lines, pricing and snapshots. A common re-bill workflow.
 * @param {string} id @returns {Promise<string>} new invoice id.
 */
export async function duplicateInvoice(id) {
  const inv = await getInvoice(id);
  if (!inv) throw toError({ code: "PGRST116" }, "Invoice not found");
  const lines = (inv.invoice_lines || []).map((l) => pick(l, LINE_WRITE_FIELDS));
  const issue = todayISO();
  return createInvoice({
    sales_order_id: null, sales_order_number: null,
    customer_id: inv.customer_id, customer_name: inv.customer_name,
    customer_email: inv.customer_email, customer_address: inv.customer_address, customer_vat: inv.customer_vat,
    issue_date: issue, due_date: dueDateFrom(issue, inv.payment_terms),
    payment_terms: inv.payment_terms, preferred_currency: inv.preferred_currency, status: "draft",
    payment_option: inv.payment_option, payment_schedule: inv.payment_schedule || [],
    business_details: inv.business_details, bank_details: inv.bank_details,
    terms_text: inv.terms_text, footer_text: inv.footer_text, notes: inv.notes,
    lines,
  });
}
